import { ExpandMore, Shield } from '@mui/icons-material';
import { ComplianceItem } from '../../data/types';

type ComplianceChecklistProps = {
  regionNote?: string;
  items: ComplianceItem[];
  expanded?: boolean;
  onToggle?: () => void;
};

/** Accordion-style preview list of compliance requirements for onboarding or summaries. */
export const ComplianceChecklist = ({
  regionNote,
  items,
  expanded = true,
  onToggle,
}: ComplianceChecklistProps) => {
  return (
    <div className="space-y-4">
      {regionNote && (
        <div className="flex gap-3 rounded-xl border border-[#5DB6CE]/20 bg-[#EFF8FA]/60 p-4">
          <Shield className="mt-0.5 shrink-0 text-primary-400" />
          <p className="font-open-sans text-sm text-[#013847]/80">{regionNote}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-[#E9E9E9] bg-white">
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <p className="font-raleway text-base font-semibold text-[#013847]">
              Kitchen compliance requirements
            </p>
            <p className="mt-1 font-open-sans text-sm text-[#808080]">
              {items.length} item{items.length === 1 ? '' : 's'} for your location
            </p>
          </div>
          <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-primary-200 px-2 font-raleway text-xs font-bold text-white">
            {items.length}
          </span>
          {onToggle && (
            <ExpandMore
              className={`ml-3 text-[#737373] transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          )}
        </button>

        {expanded && (
          <ul className="divide-y divide-[#E9E9E9] border-t border-[#E9E9E9]">
            {items.length === 0 ? (
              <li className="px-5 py-6 font-open-sans text-sm text-[#808080]">
                Add your location details to generate a tailored checklist.
              </li>
            ) : (
              items.map((item) => (
                <li key={item.id || item.item_key} className="px-5 py-4">
                  <p className="font-raleway text-sm font-semibold text-[#111111]">{item.title}</p>
                  <p className="mt-1 font-open-sans text-sm text-[#4D4D4D]">{item.description}</p>
                  {item.legal?.agency_lane && (
                    <span className="mt-2 inline-flex rounded-full bg-[#F3F4F6] px-2 py-0.5 font-open-sans text-[11px] uppercase tracking-wide text-[#737373]">
                      {item.legal.agency_lane.replace(/_/g, ' ')}
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ComplianceChecklist;
